import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useQuery } from "convex/react";
import { useCallback, useEffect, useState } from "react";
import type { OptimisticTranscriptExchange } from "../types";

function createLocalExchangeId() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  return `optimistic-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function isTerminalPromptExecutionPhase(phase: string | null | undefined) {
  return phase === "COMPLETED" || phase === "FAILED" || phase === "STOPPED";
}

export function isActivePromptExecutionPhase(phase: string | null | undefined) {
  return (
    phase === "QUEUED" ||
    phase === "PREPARING" ||
    phase === "STREAMING" ||
    phase === "AWAITING_USER_INPUT" ||
    phase === "WAITING" ||
    phase === "STOP_REQUESTED" ||
    phase === "RESUME_REQUESTED"
  );
}

export function shouldRetainOptimisticActivity(args: {
  exchange: OptimisticTranscriptExchange | null;
  threadId: string | null;
  promptExecutionPhase: string | null | undefined;
  hasCanonicalAssistantReply: boolean;
  hasPersistedActivitySection: boolean;
}) {
  if (!args.exchange || args.exchange.threadId !== args.threadId) {
    return false;
  }
  if (args.hasPersistedActivitySection) {
    return false;
  }
  if (isTerminalPromptExecutionPhase(args.promptExecutionPhase)) {
    return false;
  }
  if (args.hasCanonicalAssistantReply && !isActivePromptExecutionPhase(args.promptExecutionPhase)) {
    return false;
  }
  return true;
}

export function useOptimisticTranscriptExchange(args: {
  projectId: Id<"projects">;
  threadId: string | null;
}) {
  const [storedExchange, setExchange] = useState<OptimisticTranscriptExchange | null>(null);
  // LAT-990: read the exchange back only for the thread it was started in. The effect below
  // already cleared a foreign one, but an effect runs after the render that computed this query's
  // arguments — long enough to ask thread B about a prompt message belonging to thread A.
  const exchange =
    storedExchange && storedExchange.threadId === args.threadId ? storedExchange : null;
  const promptExecutionStatus = useQuery(
    api.claudia.getPromptExecutionStatus,
    args.threadId && exchange?.promptMessageId
      ? {
          projectId: args.projectId,
          threadId: args.threadId,
          promptMessageId: exchange.promptMessageId,
        }
      : "skip"
  );

  useEffect(() => {
    if (storedExchange && storedExchange.threadId !== args.threadId) {
      setExchange(null);
    }
  }, [args.threadId, storedExchange]);

  useEffect(() => {
    if (!exchange || exchange.threadId !== args.threadId) {
      return;
    }
    if (!isTerminalPromptExecutionPhase(promptExecutionStatus?.phase)) {
      return;
    }
    setExchange((current) => {
      if (!current || current.localExchangeId !== exchange.localExchangeId) {
        return current;
      }
      return null;
    });
  }, [args.threadId, exchange, promptExecutionStatus?.phase]);

  const startExchange = useCallback(
    (args: {
      prompt: string;
      sourceOrder: number;
      threadId: string;
      attachments?: OptimisticTranscriptExchange["attachments"];
    }) => {
      const localExchangeId = createLocalExchangeId();
      const createdAt = Date.now();
      const nextExchange: OptimisticTranscriptExchange = {
        localExchangeId,
        threadId: args.threadId,
        temporaryMessageId: `optimistic-message:${localExchangeId}`,
        prompt: args.prompt,
        attachments: args.attachments,
        sourceOrder: args.sourceOrder,
        order: args.sourceOrder * 1000,
        createdAt,
        promptMessageId: null,
        runId: null,
        pendingMode: null,
        queuedInputId: null,
      };
      setExchange(nextExchange);
      return nextExchange;
    },
    []
  );

  const resolveExchange = useCallback(
    (args: {
      localExchangeId: string;
      promptMessageId: string;
      runId: string | null;
      pendingMode?: OptimisticTranscriptExchange["pendingMode"];
      queuedInputId?: OptimisticTranscriptExchange["queuedInputId"];
    }) => {
      setExchange((current) => {
        if (!current || current.localExchangeId !== args.localExchangeId) {
          return current;
        }
        return {
          ...current,
          promptMessageId: args.promptMessageId,
          runId: args.runId,
          pendingMode: args.pendingMode ?? null,
          queuedInputId: args.queuedInputId ?? null,
        };
      });
    },
    []
  );

  const clearExchange = useCallback((localExchangeId?: string | null) => {
    setExchange((current) => {
      if (!current) {
        return current;
      }
      if (localExchangeId && current.localExchangeId !== localExchangeId) {
        return current;
      }
      return null;
    });
  }, []);

  return {
    exchange,
    startExchange,
    resolveExchange,
    clearExchange,
  };
}
